import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import moment from 'moment';
import { DATE_FORMAT } from 'app/shared/constants/input.constants';
import { map } from 'rxjs/operators';
import { createRequestOption } from 'app/shared/util/request-util';
import { IProfileRx } from 'app/shared/model/profile-rx.model';
import { NgxXml2jsonService } from 'ngx-xml2json';
import PersonDTO from 'app/shared/model/person.model';
import AdminQueueDTO from 'app/shared/model/admin-queue.model';
import UspsAddressRequest from 'app/core/usps/usps-address-request/usps-address-request.model';
import UspsAddressResponse from 'app/core/usps/usps-address-request/usps-address-response.model';
import AppConfigService from 'app/core/app-config.service';

type EntityResponseType = HttpResponse<IProfileRx>;
type EntityArrayResponseType = HttpResponse<IProfileRx[]>;

@Injectable({ providedIn: 'root' })
export default class ProfileRxService {
  public resourceUrl = `${AppConfigService.envConfig.fssServicesBaseUrl}api/profiles`;

  constructor(protected http: HttpClient, private xml2json: NgxXml2jsonService) {}

  update(profile: any): Observable<any> {
    return this.http.put(`${AppConfigService.envConfig.fssServicesBaseUrl}api/rx/rxProfile`, profile, { observe: 'response' });
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<IProfileRx>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<any>> {
    return this.http.delete<any>(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  validateBJCUser(req?: any): Observable<any> {
    return this.http.post(`${AppConfigService.envConfig.fssServicesBaseUrl}api/rx/rxProfile/validateId`, req).pipe(map((res: any) => res));
  }

  validateUser(req?: any, attempt?: number): Observable<any> {
    return this.http.post(`${AppConfigService.envConfig.fssServicesBaseUrl}api/rx/rxProfile/validation?attempt=${attempt}`, req).pipe(map((res: any) => res));
  }

  getSecurityQuestionsPlus(req: PersonDTO): Observable<any> {
    return this.http.post(`${AppConfigService.envConfig.fssServicesBaseUrl}api/rx/rxProfile/securityQuestionPlus`, req).pipe(map((res: any) => res));
  }

  submitQuestions(req?: any): Observable<any> {
    return this.http.post(`${AppConfigService.envConfig.fssServicesBaseUrl}api/rx/rxProfile/securityQuestion`, req).pipe(map((res: any) => res));
  }

  getProfile(patientId: number) {
    return this.http.get(`${AppConfigService.envConfig.fssServicesBaseUrl}api/rx/rxProfile/${patientId}`);
  }

  getDependentListWithoutPrimary(peopleSoftId): Observable<any> {
    return this.http.get(`${AppConfigService.envConfig.fssServicesBaseUrl}api/peoplesoft-imports/dependentsWithoutPrimary/${peopleSoftId}`);
  }

  sendToAdminQueue(queueItem: AdminQueueDTO): Observable<any> {
    return this.http.post(`${AppConfigService.envConfig.fssServicesBaseUrl}api/pending-queues`, queueItem, { observe: 'response' });
  }

  getPrimaryPlanMember(peopleSoftId): Observable<any> {
    return this.http.get(`${AppConfigService.envConfig.fssServicesBaseUrl}api/peoplesoft-imports/${peopleSoftId}/-1/1`, { observe: 'response' });
  }

  protected convertDateFromClient(profile: IProfileRx): IProfileRx {
    const copy: IProfileRx = { ...profile, dateOfBirth: profile.dateOfBirth != null && profile.dateOfBirth.isValid() ? moment(profile.dateOfBirth.format(DATE_FORMAT)) : null };
    return copy;
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.dateOfBirth = res.body.dateOfBirth != null ? moment(res.body.dateOfBirth) : null;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((profile: IProfileRx) => {
        profile.dateOfBirth = profile.dateOfBirth != null ? moment(profile.dateOfBirth) : null;
      });
    }
    return res;
  }

  validateAddress(addressToValidate: UspsAddressRequest): Observable<any> {
    return this.http.post(`${AppConfigService.envConfig.fssServicesBaseUrl}api/rx/rxProfile/validateaddress`, addressToValidate, { observe: 'response' });
  }

  addressDoesNotMatchUsps(userAddress: UspsAddressRequest, usps: UspsAddressResponse) {
    return (
      userAddress.streetAddress.toUpperCase() !== usps?.address?.streetAddress.toUpperCase() ||
      userAddress.city.toUpperCase() !== usps?.address?.city.toUpperCase() ||
      userAddress.ZIPCode !== usps?.address?.ZIPCode
    );
  }

  deleteAccount(id: number): Observable<any> {
    const obj = {};
    return this.http.put(`${AppConfigService.envConfig.fssServicesBaseUrl}api/users/${id}`, obj, { observe: 'response' });
  }
}
